import { RefreshCw, Save } from '@tamagui/lucide-icons';
import { withConfirm } from '@unistocks/components/confirm/with-confirm';
import { HelpText } from '@unistocks/components/form/components/help-text';
import { Row } from '@unistocks/components/layout/flex';
import { HeaderButton } from '@unistocks/components/page/header/header-button/header-button';
import { toast } from '@unistocks/components/toast';
import { AuthenticateExternalAccountModal } from '@unistocks/features/external-accounts/modals/authenticate-external-account';
import { InstrumentValidationError } from '@unistocks/features/external-accounts/models/instrument-validation-error';
import { providerEquals } from '@unistocks/features/external-accounts/utils';
import { t } from '@unistocks/localization';
import { AccountProviderDto } from '@unistocks/proxy/axios-client';
import {
  useExternalAccountsGetAccountsQuery,
  useExternalAccountsSyncExternalPortfoliosBankIdMutationWithParameters
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import { isNumber } from '@unistocks/shared/utils/util';
import React, { useCallback, useState } from 'react';
import { useControllableState } from 'tamagui';

export const ExternalAccountsHeaderRight: React.FC = () => {
  const [activeProvider, setActiveProvider] = useState<AccountProviderDto>();

  const [open, onOpenChange] = useControllableState({
    defaultProp: false,
    onChange: (open: boolean) => {
      if (open) {
        if (!activeProvider) {
          setActiveProvider(externalAccounts?.items[0]?.provider);
        }
      } else {
        setActiveProvider(undefined);
      }
    }
  });

  const { data: externalAccounts, isSuccess } = useExternalAccountsGetAccountsQuery();

  const { mutate: syncExternalAccounts } =
    useExternalAccountsSyncExternalPortfoliosBankIdMutationWithParameters({
      onSuccess: (_, { body }) => {
        toast.info(t('::Sync:Notification:InProgress:Title'), {
          description: t('::Sync:Notification:InProgress:Message', {
            0: body.displayName
          }),
          duration: 8_000
        });
      }
    });

  const onAuthenticateSuccess = useCallback(() => {
    if (activeProvider) {
      onOpenChange(false);
      syncExternalAccounts({
        provider: activeProvider.name,
        body: {
          accountIdentifier: activeProvider.accountIdentifier,
          displayName: activeProvider.displayName,
          portfolioId: null,
          providerIdentifier: activeProvider.providerIdentifier,
          syncAll: false,
          username: null,
          useTestProviders: false
        }
      });
    }

    const index = externalAccounts?.items.findIndex(
      ({ provider }) => activeProvider && providerEquals(provider, activeProvider)
    );
    const next = isNumber(index) && externalAccounts!.items[index + 1]?.provider;
    if (next) {
      setTimeout(() => {
        setActiveProvider(next);
        onOpenChange(true);
      }, 100);
    }
  }, [activeProvider, externalAccounts, onOpenChange, syncExternalAccounts]);

  const onAuthenticateError = useCallback(
    (e: any) => {
      toast.error(
        t('::ExternalAccounts:Authentication:Error{0}', { 0: activeProvider?.displayName })
      );
    },
    [activeProvider?.displayName]
  );

  return (
    <>
      {isSuccess && externalAccounts.items.length > 0 ? (
        <HeaderButton Icon={RefreshCw} onPress={() => onOpenChange(true)} />
      ) : null}

      {activeProvider ? (
        <AuthenticateExternalAccountModal
          open={open}
          onOpenChange={onOpenChange}
          provider={activeProvider}
          onAuthenticateSuccess={onAuthenticateSuccess}
          onAuthenticateError={onAuthenticateError}
        />
      ) : null}
    </>
  );
};

export type AddAccountHeaderRightProps = {
  isPending?: boolean;
  validation?: InstrumentValidationError;
  onSave?: () => void;
};

const SaveImportsButton = withConfirm(HeaderButton);

// TODO: Split info button into separate component, use in add account header
export const AddAccountHeaderRight: React.FC<AddAccountHeaderRightProps> = ({
  isPending,
  validation,
  onSave
}) => {
  return (
    <Row gap={'$3'}>
      {onSave ? (
        <SaveImportsButton
          Icon={Save}
          color={'$unistocks'}
          enabled={validation === 'Warning'}
          isLoading={isPending}
          title={t('::Import:Warning:Confirm:Title')}
          message={`${t('::Import:Warning:Confirm:Message1')}\n\n${t(
            '::Import:Warning:Confirm:Message2'
          )}\n\n${t('::Import:Warning:Confirm:Message3')}`}
          confirmText={t('::Continue')}
          onConfirm={onSave}
        />
      ) : null}

      <HelpText
        title={t('::Import:Help:Title')}
        text={t('::ExternalAccounts:Description3')}
        size={'$2.5'}
      />
    </Row>
  );
};
